import { useCallback, useEffect, useState } from "react";
import { Images, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { uploadFile } from "../../lib/field";
import { PageHeader, StatusBadge, ResponsiveTable } from "../../components/dash/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { formatDate } from "../../lib/labels";

const B = process.env.REACT_APP_BACKEND_URL;

const LandPhotos = ({ land }) => {
  const [rows, setRows] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/admin/land/${land.id}/photos`).then((r) => setRows(r.data)); }, [land.id]);
  useEffect(() => { load(); }, [load]);
  const upload = async (files) => {
    setBusy(true);
    try {
      for (const f of Array.from(files)) await uploadFile(`/admin/land/${land.id}/photos`, f, { caption });
      toast.success("Foto lahan diunggah");
      setCaption("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/land-photos/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">Foto kondisi lahan saat ini (lahan kosong, akses jalan, batas lahan, lingkungan, drone). Tampil di halaman publik bila lahan berstatus Tersedia.</p>
      <Input placeholder="Keterangan foto (mis. Akses jalan)" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="land-photo-caption-input" />
      <label className="flex items-center justify-center gap-2 h-12 rounded-md border-2 border-dashed border-emerald-700/40 text-emerald-800 font-semibold cursor-pointer" data-testid="land-photo-upload-label">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Pilih foto
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" disabled={busy} onChange={(e) => upload(e.target.files)} data-testid="land-photo-input" />
      </label>
      <div className="grid grid-cols-3 gap-2" data-testid="land-photo-grid">
        {rows?.map((m) => (
          <div key={m.id} className="relative">
            <img src={`${B}${m.url}`} alt={m.caption || "Foto lahan"} className="aspect-square w-full object-cover rounded-md border" />
            <p className="text-[11px] text-slate-600 truncate">{m.caption || "-"}</p>
            <button type="button" onClick={() => remove(m.id)} className="absolute top-1 right-1 bg-white/90 rounded p-1" aria-label="Hapus foto" data-testid={`land-photo-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-red-700" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const LandPhotosButton = ({ land, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={testid}><Images className="h-3.5 w-3.5 mr-1" />Foto</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="land-photos-dialog">
          <DialogHeader><DialogTitle>Foto Lahan {land.landCode}</DialogTitle></DialogHeader>
          {open && <LandPhotos land={land} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

const ORG_SLOTS = [["profile", "Foto Profil", "profilePhotoId"], ["cover", "Foto Banner", "coverPhotoId"], ["logo", "Logo", "logoPhotoId"]];

const OrgSlot = ({ org, kind, label, field, onSaved }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const current = org[field] ? `${B}/api/public/org-photos/${org[field]}` : null;
  const pick = (f) => { setFile(f); setPreview(f ? URL.createObjectURL(f) : null); };
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); pick(null); onSaved(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={`org-slot-${kind}`}>
      <p className="text-sm font-semibold">{label}</p>
      <div className={`${kind === "cover" ? "aspect-[3/1]" : "aspect-square w-28"} rounded-md border bg-slate-50 overflow-hidden grid place-items-center`}>
        {preview || current ? <img src={preview || current} alt={label} className="h-full w-full object-cover" data-testid={`org-slot-${kind}-img`} />
          : <span className="text-[11px] text-slate-500 px-2 text-center">Belum ada foto resmi (placeholder)</span>}
      </div>
      {preview && <p className="text-[11px] text-amber-700">Pratinjau — belum disimpan</p>}
      <div className="flex flex-wrap gap-2">
        <label className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 border rounded-md px-2.5 py-1.5 cursor-pointer">
          <Upload className="h-3.5 w-3.5" />Pilih
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => pick(e.target.files?.[0] || null)} data-testid={`org-slot-${kind}-input`} />
        </label>
        {file && <Button size="sm" disabled={busy} onClick={() => run(() => uploadFile(`/admin/farmer-groups/${org.id}/photos/${kind}`, file), "Foto disimpan")} data-testid={`org-slot-${kind}-save`}>{busy && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Simpan</Button>}
        {current && !file && <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => api.delete(`/admin/farmer-groups/${org.id}/photos/${kind}`), "Foto dihapus")} data-testid={`org-slot-${kind}-delete`}><Trash2 className="h-3.5 w-3.5 mr-1" />Hapus</Button>}
      </div>
    </div>
  );
};

export const OrgPhotosButton = ({ org, onSaved, testid }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(org);
  const reload = () => api.get("/admin/farmer-groups").then((r) => { setData(r.data.find((g) => g.id === org.id) || org); onSaved?.(); });
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => { setData(org); setOpen(true); }} data-testid={testid}><Images className="h-3.5 w-3.5 mr-1" />Foto</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="org-photos-dialog">
          <DialogHeader><DialogTitle>Foto Organisasi — {org.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Gunakan foto resmi dari PT SJI atau organisasi. Jangan gunakan foto orang acak sebagai identitas organisasi.</p>
          {ORG_SLOTS.map(([k, l, f]) => <OrgSlot key={k} org={data} kind={k} label={l} field={f} onSaved={reload} />)}
        </DialogContent>
      </Dialog>
    </>
  );
};

const InterestAction = ({ row, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(row.adminNote || "");
  const save = async (status) => {
    try { await api.patch(`/admin/interests/${row.id}`, { status, note }); toast.success("Status diperbarui"); setOpen(false); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`interest-open-${row.id}`}>Tindak lanjut</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="interest-dialog">
          <DialogHeader><DialogTitle>Minat {row.landCode} — {row.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 whitespace-pre-line">{row.message || "Tanpa pesan."}</p>
          <Textarea rows={3} placeholder="Catatan PT SJI" value={note} onChange={(e) => setNote(e.target.value)} data-testid="interest-note-input" />
          <div className="grid grid-cols-3 gap-2">
            {[["NEW", "Baru"], ["CONTACTED", "Sudah Dihubungi"], ["CLOSED", "Ditutup"]].map(([k, l]) => (
              <Button key={k} variant={row.status === k ? "default" : "outline"} onClick={() => save(k)} data-testid={`interest-status-${k}`}>{l}</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export function AdminInterests() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { api.get("/admin/interests").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-interests-page">
      <PageHeader title="Minat Investasi Kebun Kopi" subtitle="Pengajuan minat dari halaman Lahan Kopi Tersedia. Hubungi calon investor untuk menjelaskan skema kerja sama." />
      {!rows ? <Loader2 className="h-6 w-6 animate-spin text-emerald-700" /> : (
        <ResponsiveTable testid="interests-table" rows={rows} empty="Belum ada pengajuan minat investasi."
          columns={[{ key: "createdAt", label: "Tanggal", render: (r) => formatDate(r.createdAt) },
            { key: "landCode", label: "Lahan", render: (r) => <span className="font-mono text-xs">{r.landCode}</span> },
            { key: "name", label: "Nama" }, { key: "phone", label: "Telepon" }, { key: "email", label: "Email" },
            { key: "city", label: "Kota" }, { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
            { key: "act", label: "", render: (r) => <InterestAction row={r} onSaved={load} /> }]} />
      )}
    </div>
  );
}
